import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Compass, Sparkles, Map, Globe, Shield, Activity, RefreshCw, Layers, CheckCircle2, AlertCircle } from "lucide-react";

interface RoadmapPhase {
  id: string;
  title: string;
  titleSanskrit: string;
  status: "Completed" | "In Progress" | "Upcoming";
  architectureMount: string;
  technicalDetails: string;
  spiritualEthos: string;
}

const ROADMAP_PHASES: RoadmapPhase[] = [
  {
    id: "phase_1",
    title: "DSP Pitch Correction & Swaravadhana",
    titleSanskrit: "१. स्वरवधानम् (ध्वनि-शुद्धिः)",
    status: "Completed",
    architectureMount: "Mounted at bhutadamarasena.com/ancient-runes with Web Audio API real-time DSP pipeline.",
    technicalDetails: "Real-time autocorrelation pitch extraction from Web Audio API. Maps vocal frequencies to the classical Vedic pitch zones: Udatta (High), Anudatta (Low), and Svarita (Middle).",
    spiritualEthos: "Preserves the auditory purity of oral transmission (Guru-Shishya Parampara) without requiring high-bandwidth video or servers."
  },
  {
    id: "phase_2",
    title: "Cloudflare D1 SQL & WebCrypto Security",
    titleSanskrit: "२. कोष-रक्षा (अखण्ड-सुरक्षा)",
    status: "Completed",
    architectureMount: "Native Cloudflare D1 SQL database with Drizzle ORM and multi-region KV cache.",
    technicalDetails: "Zero external cluster bloat. Local AES-256-GCM browser encryption, tamper-proof session JWTs, and custom bitwise cryptographic hash integrity.",
    spiritualEthos: "Guarantees complete sovereignty and zero data leakage for private sadhana logs and spiritual commitments."
  },
  {
    id: "phase_3",
    title: "Agni Hawan & Tarpana Sadhana Engine",
    titleSanskrit: "३. अग्नि हवनशाला तथा तर्पणम्",
    status: "Completed",
    architectureMount: "Direct interactive guidance modules embedded into the monolithic liturgy framework.",
    technicalDetails: "Full interactive Agni-Homa steps (Achamanam, Pranayama, Pavana Kriya, Ajya Aahuti, Purnahuti) with dynamic Astro-Hora calculations synchronized to local system time.",
    spiritualEthos: "Bridges strict ancient Shastra rules with the practical realities of modern urban apartments, using Manasa Puja (mental focus) as the master key."
  },
  {
    id: "phase_4",
    title: "Self-MoR & Stripe Tier Synchronization",
    titleSanskrit: "४. अधिकार-व्यवस्था (शुल्क-प्रबन्धनम्)",
    status: "Completed",
    architectureMount: "Self-MoR via Stripe Checkout and Customer Billing Portal under Aghora Abraham Global LLC.",
    technicalDetails: "Direct Stripe webhooks into Cloudflare D1 database. Manages GENERAL, PRO, and LIFETIME_FOUNDER entitlement tiers across the bhutadamarasena.com domain.",
    spiritualEthos: "Ensures transparent, ethical merchant of record operations directly funding monastic R&D laboratories."
  },
  {
    id: "phase_5",
    title: "Astro-Hora & Panchanga Scheduler",
    titleSanskrit: "५. काल-विधानम् (पञ्चाङ्ग-गणना)",
    status: "In Progress",
    architectureMount: "Microtonal planetary frequency synchronizer running at the edge.",
    technicalDetails: "Astronomical coordinate calculations to calculate precise local Sunrise, Sunset, Sandhyavandhanam times, Tithi, Nakshatra, and customized Yoga patterns.",
    spiritualEthos: "Aligns ritual sadhana with the breath of the cosmos, performing each offering at the exact moment of natural cosmic alignment."
  }
];

export default function RitualRoadmap() {
  const [activePhaseId, setActivePhaseId] = useState("phase_1");
  const selectedPhase = ROADMAP_PHASES.find(p => p.id === activePhaseId) || ROADMAP_PHASES[0];

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 shadow-xl" id="ritual-roadmap-panel">
      {/* Title */}
      <div className="flex items-center gap-3 border-b border-neutral-800 pb-4 mb-6">
        <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-400">
          <Map className="w-5 h-5 fill-amber-400/20" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-neutral-100 flex items-center gap-2">
            Vedic Karmakāṇḍa Roadmap
            <span className="text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full px-2 py-0.5">
              Strict & Compliant
            </span>
          </h3>
          <p className="text-xs text-neutral-500">
            A precise developmental blueprint to restore and democratize Sanskrit oral lineages
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Phase List Tree */}
        <div className="md:col-span-5 flex flex-col gap-2 border-r border-neutral-800/50 pr-2">
          {ROADMAP_PHASES.map((phase) => {
            const isActive = phase.id === activePhaseId;
            const statusColor =
              phase.status === "Completed"
                ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                : phase.status === "In Progress"
                ? "bg-amber-500/15 text-amber-400 border-amber-500/20"
                : "bg-neutral-800 text-neutral-500 border-neutral-850";

            return (
              <button
                key={phase.id}
                onClick={() => setActivePhaseId(phase.id)}
                className={`text-left p-3.5 rounded-xl border transition-all duration-300 cursor-pointer select-none flex flex-col gap-1.5 ${
                  isActive
                    ? "bg-neutral-950 border-amber-500/30 shadow-md"
                    : "bg-neutral-900/40 hover:bg-neutral-800/40 border-transparent"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${statusColor}`}>
                    {phase.status}
                  </span>
                  <span className="text-[9px] font-mono text-neutral-500">
                    {phase.id.toUpperCase().replace("_", " ")}
                  </span>
                </div>
                <div>
                  <h4 className={`text-xs font-bold transition-colors ${isActive ? "text-amber-400" : "text-neutral-300"}`}>
                    {phase.title}
                  </h4>
                  <p className="text-[10px] text-neutral-500 font-normal italic mt-0.5">
                    {phase.titleSanskrit}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Phase Details Description */}
        <div className="md:col-span-7 bg-neutral-950 border border-neutral-850 rounded-xl p-5 relative overflow-hidden flex flex-col justify-between">
          
          <div className="absolute top-0 right-0 p-8 opacity-[0.02] text-amber-500 pointer-events-none">
            <Compass className="w-48 h-48 animate-spin-slow" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-mono font-bold text-amber-500">
                🔍 Phase Specifics
              </span>
              <div className="flex items-center gap-1.5 bg-neutral-900 px-2.5 py-1 rounded-lg border border-neutral-800 text-[10px] text-neutral-400">
                <Activity className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
                <span>Dev Status: Active</span>
              </div>
            </div>

            {/* Spiritual Ethos Section */}
            <div className="mb-4">
              <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider block mb-1">
                Spiritual Ethos & Lineage Intent
              </span>
              <p className="text-xs text-neutral-300 leading-relaxed bg-neutral-900/40 p-3 rounded-lg border border-neutral-850/60">
                {selectedPhase.spiritualEthos}
              </p>
            </div>

            {/* Technical Logic Section */}
            <div className="mb-4">
              <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider block mb-1">
                Technical Logic & Execution
              </span>
              <p className="text-xs text-neutral-400 leading-relaxed font-sans">
                {selectedPhase.technicalDetails}
              </p>
            </div>

            {/* Monolith Deployment Section */}
            <div>
              <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider block mb-1">
                Monolith Mount & Edge Target
              </span>
              <p className="text-xs text-amber-300 bg-amber-950/15 border border-amber-500/10 p-3 rounded-lg leading-relaxed font-mono">
                {selectedPhase.architectureMount}
              </p>
            </div>
          </div>

          <div className="mt-5 border-t border-neutral-850 pt-4 flex items-center justify-between text-[10px] text-neutral-500">
            <div className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-neutral-600" />
              <span>Compliant with traditional Karmakanda lineages</span>
            </div>
            <span className="font-mono text-neutral-600">
              V{activePhaseId === "phase_1" || activePhaseId === "phase_2" || activePhaseId === "phase_3" ? "1.0.0" : "1.1.0-beta"}
            </span>
          </div>

        </div>
      </div>
    </div>
  );
}
